// Core
import React, { useMemo } from 'react';

// Libraries
import { useTranslation } from 'react-i18next';

// Plugins
import { countActiveAlarms } from '../../../../../plugins/crusher';

// Types
import { AlarmStatus } from '../../../../../types/global';
import { EquipmentUnitDataConeCrusher } from '../../../../../types/crusher';

// Components
import { Drawer, DrawerSize } from '../../../../../layout/elements/Drawer';
import { NavigationTabs } from '../../../../../layout/elements/NavigationTabs';
import { Table, TableProps } from '../../../../../layout/elements/Table';

// Component
import { HMIAlarmsProps } from './HMIAlarms.types';
import { NavigationTabsContainer, TableContainer } from './HMIAlarms.style';

function HMIAlarms({
  className,
  isDrawerOpen,
  onCloseDrawer,
  unitData,
  testId,
}: HMIAlarmsProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();

  // Get count of active alarms
  const alarmActives: number = countActiveAlarms(unitData);

  // Navigation tabs configuration
  const changeHmiAlarms: { key: string; label: string }[] = [
    {
      key: 'alarm',
      label: `${t('view.crusher.alarms.drawer.tabs.alarmActive')} (${alarmActives})`,
    },
  ];

  /* ***********************************************************************************************
  ****************************************** METHODS ***********************************************
  *********************************************************************************************** */

  // Extract active alarms from operational data
  const activeAlarmsData: { key: string; message: string }[] = useMemo(() => {
    if (!unitData) {
      return [];
    }

    const alarms: { key: string; message: string }[] = [];

    // Iterate through all properties and find alarm fields that are active
    Object.keys(unitData).forEach((key: string) => {
      const value = unitData[key as keyof EquipmentUnitDataConeCrusher];

      // Check if it's an alarm field (starts with 'alm') and has active status
      if (key.startsWith('alm') && value === AlarmStatus.active) {
        alarms.push({
          key: `${unitData.idEquipmentUnit}-${key}`,
          message: key,
        });
      }
    });

    return alarms;
  }, [unitData]);

  /* ********** Table **********  */

  const columns: TableProps['columns'] = [
    {
      title: `${t('view.crusher.alarms.drawer.message')}`,
      dataIndex: 'message',
      key: 'message',
      sorter: true,
    },
  ];

  const getTableData = (): TableProps['rows'] => activeAlarmsData.map((alarm) => ({
    key: alarm.key,
    message: `${t(`view.crusher.alarms.drawer.${alarm.message}`)}`,
  }));

  /* ***********************************************************************************************
  ************************************** COMPONENT HANDLING ****************************************
  *********************************************************************************************** */

  return (
    <Drawer
      testId={testId}
      className={className}
      title={t('view.crusher.alarms.drawer.title')}
      open={isDrawerOpen}
      onClose={onCloseDrawer}
      footer={false}
      size={DrawerSize.lg}
    >
      <NavigationTabsContainer>
        <NavigationTabs
          items={changeHmiAlarms}
          onClick={() => {}} // @TODO: Implement tab switching when adding history functionality
          selected="alarm"
        />
      </NavigationTabsContainer>
      <TableContainer>
        <Table
          columns={columns}
          rows={getTableData()}
        />
      </TableContainer>
    </Drawer>
  );
}

export { HMIAlarms };
